import { Routes, UrlSegment } from '@angular/router';
import { Login } from './auth/login/login';
import { Layout } from './ui/layout/layout';
import { Dashboard } from './features/dashboard/dashboard';
import { Register } from './auth/register/register';
import { AuthLayout } from './auth/auth-layout/auth-layout';
import { SuccessRegistration } from './auth/success-registration/success-registration';
import { ForgotPassword } from './auth/forgot-password/forgot-password';
import { ResetPassword } from './auth/reset-password/reset-password';
import { authGuard } from './shared/guards/auht.guard';
import { membershipGuard } from './shared/guards/membership.guard';
import { featureGuard } from './shared/guards/feature.guard';
import { Profile } from './features/user/profile/profile';
import { AccountSetting } from './features/user/account-setting/account-setting';
import { Support } from './features/user/support/support';
import { AdminSupport } from './features/system-admin/support/support';
import { Agendas } from './features/agendas/agendas';
import { WalkIns } from './features/agendas/walk-ins/walk-ins';
import { People } from './features/people/people';
import { VerifyEmail } from './auth/verify-email/verify-email';
import { Staff } from './features/staff/staff';
import { Inventory } from './features/inventory/inventory';
import { Sales } from './features/sales/sales';
import { Finance } from './features/finance/finance';
import { Services } from './features/services/services';
import { Reminders } from './features/reminders/reminders';
import { EMarketing } from './features/emarketing/emarketing';
import { WA } from './features/wa/wa';
import { Promos } from './features/promos/promos';
import { Bundles } from './features/bundles/bundles';
import { Settings } from './features/settings/settings';
import { Business } from './features/settings/business/business';
import { General } from './features/settings/general/general';
import { Schedules } from './features/settings/schedules/schedules';
import { BusinessHours } from './features/settings/business-hours/business-hours';
import { Users } from './features/users/users';
import { Users as UsersSettingsComponent } from './features/settings/users/users';
import { PeopleProfile } from './features/people/people-profile/people-profile';
import { Membership } from './features/settings/membership/membership';
import { Messages } from './features/settings/messages/messages';
import { MembershipExpiredComponent } from './features/membership/expired/expired.component';
import { redirectIfAuthGuard } from './shared/guards/redirect-if-auth.guard';
import { Reports } from './features/reports/reports';
import { PublicBooking } from './features/public-booking/public-booking';
import { BookingConfirmation } from './features/public-booking/booking-confirmation/booking-confirmation';
import { PublicEventComponent } from './features/public-event/public-event';
import { BookingRequests } from './features/agendas/booking-requests/booking-requests';
import { SystemAdmin } from './features/system-admin/system-admin';
import { AdminDashboard } from './features/system-admin/dashboard/dashboard';
import { AdminBusinesses } from './features/system-admin/businesses/businesses';
import { AdminAuditLog } from './features/system-admin/audit-log/audit-log';
import { BusinessTypes } from './features/system-admin/business-types/business-types';
import { Countries } from './features/system-admin/countries/countries';
import { NotFound } from './features/not-found/not-found';
import { AcceptInvitation } from './auth/accept-invitation/accept-invitation';
import { INVITATION_SLUG_PATTERN } from './services/team/team.service';

/** /{business-slug}--{token}: business booking slugs never contain "--", so both share the root */
export function invitationMatcher(segments: UrlSegment[]) {
  return segments.length === 1 && INVITATION_SLUG_PATTERN.test(segments[0].path)
    ? { consumed: segments, posParams: { slug: segments[0] } }
    : null;
}

export const routes: Routes = [
  {
    path: '',
    redirectTo: 'auth/signin',
    pathMatch: 'full',
  },
  {
    path: ':slug/confirmation',
    component: BookingConfirmation,
  },
  {
    path: 'booking/manage/:eventId',
    component: PublicEventComponent,
  },
  {
    path: 'auth/verify-email',
    component: AuthLayout,
    children: [{ path: '', component: VerifyEmail }],
  },
  {
    path: 'auth',
    component: AuthLayout,
    canMatch: [redirectIfAuthGuard],
    children: [
      { path: 'signin', component: Login },
      { path: 'signup', component: Register },
      { path: 'forgot-password', component: ForgotPassword },
      { path: 'reset-password', component: ResetPassword },
      { path: 'success', component: SuccessRegistration },
    ],
  },
  {
    path: 'membership-expired',
    component: MembershipExpiredComponent,
    canActivate: [authGuard],
  },
  {
    path: '',
    component: Layout,
    children: [
      {
        path: 'dashboard',
        component: Dashboard,
        canActivate: [authGuard, membershipGuard],

      },
      {
        path: 'agendas',
        component: Agendas,
        canActivate: [authGuard, membershipGuard],

      },
      {
        path: 'agendas/walk-ins',
        component: WalkIns,
        canActivate: [authGuard, membershipGuard],
      },
      {
        path: 'agendas/requests',
        component: BookingRequests,
        canActivate: [authGuard, membershipGuard],

      },
      {
        path: 'people',
        children: [
          { path: '', component: People },
          { path: ':userId', component: PeopleProfile },
        ],
        canActivate: [authGuard, membershipGuard],

      },
      {
        path: 'reports',
        component: Reports,
        canActivate: [authGuard, membershipGuard],

      },
      {
        path: 'professionals',
        component: Staff,
        canActivate: [authGuard, membershipGuard],

      },
      {
        path: 'schedules',
        component: Schedules,
        canActivate: [authGuard, membershipGuard],

      },
      {
        path: 'business-hours',
        component: BusinessHours,
      },
      {
        path: 'services',
        component: Services,
        canActivate: [authGuard, membershipGuard],

      },
      {
        path: 'promos',
        component: Promos,
        canActivate: [authGuard, membershipGuard, featureGuard],

      },
      {
        path: 'inventory',
        component: Inventory,
        canActivate: [authGuard, membershipGuard, featureGuard],

      },
      {
        path: 'sales',
        component: Sales,
        canActivate: [authGuard, membershipGuard, featureGuard],

      },
      {
        path: 'settings',
        component: Settings,
        canActivate: [authGuard, membershipGuard],

        children: [
          {
            path: '',
            component: General,
          },

          {
            path: 'schedules',
            component: Schedules,
          },
          {
            path: 'business-hours',
            component: BusinessHours,
          },
          {
            path: 'professionals',
            component: Staff,
          },
          {
            path: 'services',
            component: Services,
          },
          {
            path: 'reports',
            component: Reports,
          },
          {
            path: 'messages',
            component: Messages,
          },
          {
            path: 'users',
            component: UsersSettingsComponent,
          },
        ],
      },
      {
        path: 'settings/billing',
        component: Membership,
        canActivate: [authGuard],

      },
      {
        path: 'user',
        canActivate: [authGuard, membershipGuard],
        children: [
          {
            path: 'profile',
            component: Profile,
          },
          {
            path: 'account-settings',
            component: AccountSetting,
          },
          {
            path: 'support',
            component: Support,
          },
        ],

      },
      {
        path: 'system-admin',
        component: SystemAdmin,
        canActivate: [authGuard],
        children: [
          { path: '', component: AdminDashboard },
          { path: 'businesses', component: AdminBusinesses },
          { path: 'audit-log', component: AdminAuditLog },
          { path: 'support', component: AdminSupport },
          { path: 'catalogs/business-types', component: BusinessTypes },
          { path: 'catalogs/countries', component: Countries },
        ],
      },
    ],
  },
  {
    matcher: invitationMatcher,
    component: AuthLayout,
    children: [{ path: '', component: AcceptInvitation }],
  },
  {
    path: ':slug',
    component: PublicBooking,
  },
  {
    path: '**',
    component: NotFound,
  },
];
